import { Button, Chip, IconButton, Link, Skeleton, Stack, Typography } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import ChevronRightIcon from '@/components/Icons/ChevronRightIcon';
import ExternalLinkIcon from '@/components/Icons/ExternalLinkIcon';
import LockIcon from '@/components/Icons/LockIcon';
import { Levels } from '@/graphql';
import useIsTrainingEnabled from '@/hooks/useIsTrainingEnabled';
import useOrg from '@/hooks/useOrg';
import { links } from '@/localization';
import { BasetenPageEnum } from '@/routes';

import Training from '../../Training';
import { MAX_ITEMS_TO_SHOW } from '../../Training/constants';
import { useTrainingProjects } from '../../Training/hooks';
import { WorkspaceSections } from '../../types';
import { AppLink, EmptyState, Section, TrainingAnimationContainer, ViewAllItems } from '../styled';

function Trainings() {
  const { trainingProjects, loading } = useTrainingProjects(true);
  const isTrainingEnabled = useIsTrainingEnabled();
  const org = useOrg();

  function getContent() {
    if (loading) {
      return (
        <Stack pt={1}>
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="40%" />
        </Stack>
      );
    }

    if (isEmpty(trainingProjects)) {
      return (
        <EmptyState gap={8}>
          <Stack gap={3}>
            <Stack gap={1} maxWidth={600}>
              <Typography variant="subtitle1">Start training and fine-tuning on Baseten</Typography>
              <Typography variant="body1" color="text.muted">
                Run reproducible, scalable training jobs with full control over data, code, compute,
                and runtime settings.{' '}
                <Link href={links.basetenDocsTrainingOverview} target="_blank">
                  Learn more
                </Link>
              </Typography>
            </Stack>
            {isTrainingEnabled ||
            org.tierName === Levels.Startup ||
            org.tierName === Levels.Business ? (
              <Button
                variant="outlined"
                href={links.basetenDocsTrainingOverview}
                endIcon={<ExternalLinkIcon />}
                target="_blank"
                sx={{ width: 'fit-content' }}
              >
                Train or fine-tune a model
              </Button>
            ) : (
              <Button
                variant="outlined"
                href="https://www.baseten.co/talk-to-us/request-access/"
                target="_blank"
                sx={{ width: 'fit-content' }}
                startIcon={<LockIcon />}
              >
                Request early access
              </Button>
            )}
          </Stack>
          <TrainingAnimationContainer />
        </EmptyState>
      );
    }

    return (
      <Stack gap={2} mt={-3}>
        <Stack mx={-4}>
          <Training minimal />
        </Stack>
        {trainingProjects.length > MAX_ITEMS_TO_SHOW && (
          <AppLink
            page={BasetenPageEnum.Workspace}
            params={{ section: WorkspaceSections.Training }}
          >
            <ViewAllItems>View all training projects</ViewAllItems>
          </AppLink>
        )}
      </Stack>
    );
  }

  return (
    <Section>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack gap={1} height={54}>
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="h3">Training projects</Typography>
            <Chip label="Early access" size="small" />
          </Stack>
          {!isEmpty(trainingProjects) && (
            <Typography variant="body1" color="text.muted">
              Run reproducible, scalable training jobs with full control.
            </Typography>
          )}
        </Stack>
        <AppLink page={BasetenPageEnum.Workspace} params={{ section: WorkspaceSections.Training }}>
          <IconButton data-cy="list-item-arrow">
            <ChevronRightIcon />
          </IconButton>
        </AppLink>
      </Stack>
      {getContent()}
    </Section>
  );
}

export default Trainings;
